import { Anchor, Badge, Group, Loader, Stack, Table, Text, Title } from "@mantine/core";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";

import {
  getListStaffUsageQueryKey,
  listStaffUsage,
} from "../../api/generated/endpoints/staff/staff";
import type {
  StaffUsagePage,
  UsageGroup,
  UsageNames,
  UsageRow,
} from "../../api/generated/model";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { countOf, formatCount } from "../../ui/format";
import { DailyCharts, UsageSummary } from "./UsageCharts";
import { UsageFilters } from "./UsageFilters";
import { Flagged, NO_KEY, Unknown, formatUsd, formatUtcDay, keyLabel } from "./usage-format";
import { GROUPS, type UsageSearch, filtersOf, rangeOf, usageSearch } from "./usage-search";
import classes from "./usage.module.css";

/** Each row's measures (spec usage-analytics 3), with what each counts. */
const MEASURES: { key: Exclude<keyof UsageRow, "key">; label: string; hint: string }[] = [
  { key: "generations", label: "Generations", hint: "Jobs with no parent" },
  { key: "jobs", label: "Jobs", hint: "With their steps" },
  { key: "open", label: "Open", hint: "Not settled yet" },
  { key: "succeeded", label: "Succeeded", hint: "Generations that succeeded" },
  { key: "failed", label: "Failed", hint: "Failed, expired or canceled" },
  { key: "delivered", label: "Outputs", hint: "Outputs delivered" },
  { key: "charged", label: "Charged", hint: "Credits charged" },
  { key: "refunded", label: "Refunded", hint: "Credits given back" },
  { key: "net", label: "Net credits", hint: "Charged less refunded" },
  { key: "vendor_cost_usd", label: "Vendor cost", hint: "The known cost in USD" },
  { key: "cost_incomplete", label: "Cost incomplete", hint: "Jobs with a request whose cost is unknown" },
  { key: "unpriced", label: "Unpriced", hint: "Jobs whose charge met an unpriced SKU" },
];

/**
 * A usage page with the search that asked for it, kept on screen while the next one loads: so a
 * page is drawn by its own grouping, days and links, never by a newer search's (a user's row read
 * as a day while the days load). Its key is its own, beside the generated hook's.
 */
function useUsage(search: UsageSearch, groupBy: UsageGroup) {
  const params = { ...filtersOf(search), group_by: groupBy };
  // The search as this page's own: the charts' read by day serves the table by day too. Its own
  // keys alone, so a kept row's link takes the frame on screen, not the one it was read in (`?ui=`,
  // which the staff route keeps; spec ui-perf/admin-panel-pro-simple).
  const asked = { ...usageSearch.parse(search), by: groupBy === "user" ? undefined : groupBy };
  return useQuery<{ page: StaffUsagePage; search: UsageSearch }>({
    queryKey: [...getListStaffUsageQueryKey(params), "asked"],
    queryFn: ({ signal }) => listStaffUsage(params, { signal }).then((page) => ({ page, search: asked })),
    placeholderData: keepPreviousData,
  });
}

const dimmed = (kept: boolean) => ({ opacity: kept ? 0.6 : 1 });

/**
 * Staff's Usage (spec usage-analytics 3, the owner's v1 mockup of 2026-10-28): every team's
 * generations over the filters, grouped, with their totals, a summary and two daily charts. A
 * row opens its generations. No prompt or file shows here: those are in a generation's trace.
 */
export function StaffUsageScreen() {
  const search = useSearch({ from: "/signed-in/staff/usage" });
  const navigate = useNavigate({ from: "/staff/usage" });
  const usage = useUsage(search, search.by ?? "user");
  // The charts read the same filters by day: one page holds a range's every day.
  const daily = useUsage(search, "day");
  const shown = usage.isError ? undefined : usage.data;
  const days = daily.isError ? undefined : daily.data;
  // One refusal says it once: both reads take the same filters.
  const error = usage.error ?? daily.error;
  return (
    <Stack gap="lg">
      <UsageFilters
        search={search}
        onChange={(next) => void navigate({ search: next, replace: true })}
      />
      {usage.isPending ? <Loader size="sm" aria-label="Loading usage" /> : null}
      {error === null ? null : <ErrorAlert error={error} />}
      {shown === undefined ? null : (
        // A refetch keeps the last answer on screen, dimmed, until the new one lands.
        <Stack gap="lg">
          <div style={dimmed(usage.isPlaceholderData)}>
            <UsageSummary total={shown.page.total} />
          </div>
          {days === undefined ? null : (
            <div style={dimmed(daily.isPlaceholderData)}>
              <DailyCharts {...rangeOf(days.search)} rows={days.page.rows} />
            </div>
          )}
          <div style={dimmed(usage.isPlaceholderData)}>
            <UsageTable page={shown.page} search={shown.search} />
          </div>
        </Stack>
      )}
    </Stack>
  );
}

/** A page's rows, by the grouping and the search that asked for them. */
function UsageTable(props: { page: StaffUsagePage; search: UsageSearch }) {
  const { page, search } = props;
  const by = search.by ?? "user";
  const { from, to } = rangeOf(search);
  const group = GROUPS.find((each) => each.value === by)?.label ?? by;
  return (
    <Stack gap="xs">
      <Group justify="space-between" align="baseline">
        <Title order={2} size="h4">
          Usage by {group.toLowerCase()}
        </Title>
        <Text size="sm" c="var(--cai-color-text-muted)">
          {countOf(page.rows.length, "row")} · {formatUtcDay(from)} – {formatUtcDay(to)}
        </Text>
      </Group>
      {page.rows.length === 0 ? (
        <Text>No generations match these filters.</Text>
      ) : (
        <Table.ScrollContainer minWidth={1180}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{by === "day" ? "Day (UTC)" : group}</Table.Th>
                {MEASURES.map((measure) => (
                  <Table.Th key={measure.key} className={classes.num} title={measure.hint}>
                    {measure.label}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {page.rows.map((row) => (
                <Table.Tr key={row.key ?? ""}>
                  <Table.Td>
                    <GroupCell by={by} value={row.key} names={page.names} search={search} />
                  </Table.Td>
                  <Measures row={row} />
                </Table.Tr>
              ))}
            </Table.Tbody>
            <Table.Tfoot>
              <Table.Tr fw={700}>
                <Table.Td>Total</Table.Td>
                <Measures row={page.total} />
              </Table.Tr>
            </Table.Tfoot>
          </Table>
        </Table.ScrollContainer>
      )}
      {page.more ? (
        <Text size="sm">Only the first 500 groups show: narrow the filters to see the rest.</Text>
      ) : null}
      <Text size="sm" c="var(--cai-color-text-muted)">
        Most net credits first; days newest first. A generation counts on the UTC day it started,
        with its steps, and a refund changes its own day, so a past day can still move: the ledger
        stays the books. A brand shows as its id, since staff do not read brands in v1.
      </Text>
    </Stack>
  );
}

/** A row's group: a link to its generations, or, with no key, words that open nothing. */
function GroupCell(props: {
  by: UsageGroup;
  value: string | null;
  names: UsageNames;
  search: UsageSearch;
}) {
  const { by, value, names, search } = props;
  if (value === null) return <Text c="var(--cai-color-text-muted)">{NO_KEY[by]}</Text>;
  const user = by === "user" ? names.users[value] : undefined;
  const label = keyLabel(by, value, names);
  return (
    <Stack gap={0}>
      <Anchor
        renderRoot={(anchor) => (
          <Link {...anchor} to="/staff/usage/generations" search={{ ...search, key: value }} />
        )}
      >
        {label}
      </Anchor>
      {user !== undefined && user.email !== label ? (
        <Text size="xs" c="var(--cai-color-text-muted)">
          {user.email}
        </Text>
      ) : null}
      {user?.deleted_at ? (
        <Badge size="xs" color="gray" variant="light">
          Deleted
        </Badge>
      ) : null}
      {(by === "model" || by === "type") && label !== value ? (
        <Text size="xs" c="var(--cai-color-text-muted)">
          {value}
        </Text>
      ) : null}
    </Stack>
  );
}

function Measures({ row }: { row: UsageRow }) {
  return MEASURES.map(({ key }) => {
    const value = row[key];
    if (key === "vendor_cost_usd") {
      return (
        <Table.Td key={key} className={classes.num}>
          {typeof value === "string" ? formatUsd(value) : <Unknown />}
        </Table.Td>
      );
    }
    const count = typeof value === "number" ? value : 0;
    return (
      <Table.Td key={key} className={classes.num}>
        {key === "cost_incomplete" || key === "unpriced" ? (
          <Flagged count={count} />
        ) : (
          <span className={count === 0 ? classes.zero : undefined}>{formatCount(count)}</span>
        )}
      </Table.Td>
    );
  });
}
